'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { useCustomerPanelTranslations } from '@/lib/customer-panel-utils';
import { cardVariants, listItemVariants } from '@/lib/animations';

interface ActivityItem {
  type: string;
  title: string;
  date: string;
  status: string;
}

interface RecentActivityProps {
  activities: ActivityItem[];
  translations?: Record<string, string>;
  index?: number;
}

export default function RecentActivity({
  activities,
  translations = {},
  index = 0,
}: RecentActivityProps) {
  const t = useCustomerPanelTranslations(translations);

  const activityTitle = t('customer_panel.dashboard.recent_activity', 'Recent Activity');

  if (activities.length === 0) {
    return (
      <motion.div 
        className="bg-white rounded-lg shadow-tour-card border border-gray-100 p-8 group group-hover:shadow-2xl transition-all duration-300 transition-shadow duration-500"
        role="region" 
        aria-label={activityTitle}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        custom={index}
      >
        <h3 className="text-lg font-bold text-gray-900 mb-6">{activityTitle}</h3>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-500" role="status">
            {t('customer_panel.dashboard.no_activity', 'No activity yet.')}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {t('customer_panel.dashboard.no_activity_subtitle', 'Your recent activities will appear here')}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="bg-white rounded-lg shadow-tour-card border border-gray-100 p-6 group group-hover:shadow-2xl transition-all duration-300 transition-shadow duration-500"
      role="region" 
      aria-label={activityTitle}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      custom={index}
    >
      <h3 className="text-lg font-bold text-gray-900 mb-5">{activityTitle}</h3>
      <ul className="space-y-3" role="list">
        {activities.map((activity, itemIndex) => (
          <motion.li 
            key={itemIndex} 
            className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-all duration-200"
            variants={listItemVariants}
            initial="hidden"
            animate="visible"
            custom={itemIndex}
            whileHover={{ x: 4 }}
          >
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">{activity.title}</p>
              <time className="text-xs text-gray-500 mt-1 flex items-center gap-1" dateTime={activity.date}>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {activity.date}
              </time>
            </div>
            <span 
              className={`px-3 py-1.5 text-xs font-semibold rounded-full shadow-sm ${
                activity.status === 'confirmed' ? 'bg-gradient-to-r from-green-100 to-green-50 text-green-700 border border-green-200' :
                activity.status === 'pending' ? 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-700 border border-yellow-200' :
                'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-700 border border-gray-200'
              }`}
              aria-label={`Status: ${activity.status}`}
            >
              {activity.status}
            </span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );
}

